import { cn } from "cn";
import { BwTagLine, BwUserInformationIdLine } from "@/components/ui/icons";
import { usePresaleCouponDetail } from "../../_api/use-presale-coupon-detail";
import type { PresaleMembership } from "../../_api/use-presale-membership";

/**
 * The membership the account step is reserving, plus the campaign coupon when the URL
 * carries one — the presale twin of `features/join`'s sign-up card.
 *
 * A deliberate copy of that card rather than a shared component: this is occurrence two,
 * and the two funnels already answer to different concepts — join sells a charged catalog
 * plan, presale reserves a founding membership until launch day. Promote to
 * `features/shared` when a third one appears, not before.
 *
 * A resolved coupon REPLACES the price line, exactly as join does: what the buyer pays
 * with the code applied is not a number this card computes, and an undiscounted price
 * sitting beside a coupon name reads as the price they will be charged.
 */
export function PresalePlanCard({
  membership,
  discountCode,
}: {
  membership: PresaleMembership;
  discountCode: string;
}) {
  const { data: coupon } = usePresaleCouponDetail(discountCode);

  return (
    <div
      className={cn(
        /* sorted */
        {
          "border border-border-amber-min bg-bg-amber-min rounded-xl p-1 pt-0":
            coupon,
        },
      )}
    >
      {coupon && (
        <div
          className={cn(
            /* sorted */
            "flex items-center justify-center gap-1",
            "p-1",
          )}
        >
          <BwTagLine size={20} className="text-text-amber" />
          <p className="text-sm font-medium text-text-amber">
            {coupon.coupon?.name}
          </p>
        </div>
      )}

      <div
        className={cn(
          /* sorted */
          "flex items-center gap-4",
          "p-4",
          "bg-bg-light border border-border-dark-low rounded-xl",
          {
            "shadow-md": !coupon,
          },
        )}
      >
        <BwUserInformationIdLine
          size={24}
          className="text-text-primary shrink-0"
        />
        <div className="flex flex-col grow items-start">
          <div
            className={cn(
              /* sorted */
              "flex items-center justify-between",
              "w-full",
              "text-sm font-medium",
            )}
          >
            <p className="grow">{membership.name}</p>
            {!coupon && (
              <p>{`${membership.formattedPrice ?? ""} / ${membership.interval ?? ""}`}</p>
            )}
          </div>
          <p className="text-sm text-text-dark-high">60-day guarantee.</p>
        </div>
      </div>
    </div>
  );
}
